import React, { useRef, useState } from "react";
import { IconButton, Modal, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
    ModalWrapper,
    ButtonWrapper,
    TitleText,
    WarningText,
    TitleWrapper,
    StyledBackdrop,
    ReasonText,
} from "./Styled";
import CustomButton from "../TitleRow/CustomButton";
import TextNote from "../TextNote";

interface FailModalProps {
    open: boolean;
    handleClose: () => void;
    handleConfirm: (text?: string, cancel?: boolean) => Promise<void>;
    title?: string;
    contentText?: string[];
    reasonText?: string;
    buttonText?: string;
    isLoading?: boolean;
    children?: React.ReactNode;
    helperText?: string;
    cancelButtonText?: string;
    confirmColor?: string;
    managed?: boolean;
}

const FailModal = ({
    open,
    handleConfirm,
    handleClose,
    title,
    contentText = [],  // Default to empty array if no contentText is passed
    reasonText,
    buttonText,
    isLoading = false,
    children,
    helperText,
    cancelButtonText = "Đóng",
    confirmColor,
    managed = false,
}: FailModalProps) => {
    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const busy = isLoading || submitting;

    const handleCloseWithAnimation = () => {
        if (busy || submittingRef.current) return;
        if (managed) handleClose();
        else setTimeout(handleClose, 100);
    };

    const handleConfirmWithAnimation = async (cancel?: boolean) => {
        if (busy || submittingRef.current) return;
        if (managed) {
            if (!text.trim()) return;
            submittingRef.current = true;
            setSubmitting(true);
            try {
                await handleConfirm(text.trim(), cancel);
            } finally {
                submittingRef.current = false;
                setSubmitting(false);
            }
            return;
        }
        const reason = text.slice(0, 1000); // Limit the length of the reason to 1000 characters
        void handleConfirm(reason, cancel);
        setTimeout(handleClose, 100);
    };

    // Calculate padding-top based on the number of items in the contentText array
    const getPaddingTop = (index: number) => {
        return index === 0 ? "24px" : "8px"; // 24px for the first item, 8px for others
    };

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            sx={managed ? { display: "flex", alignItems: "center", justifyContent: "center", p: 2, overflowY: "auto" } : undefined}
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                sx: managed ? { position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.56)", backdropFilter: "blur(4px)" } : undefined,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={open} role={managed ? "dialog" : undefined} aria-modal={managed || undefined} aria-label={managed ? title : undefined}
                style={managed ? { position: "relative", top: "auto", left: "auto", transform: "none", animation: "none", opacity: 1, width: "100%", maxWidth: 520, overflowY: "auto", maxHeight: "calc(100dvh - 32px)", boxSizing: "border-box", fontFamily: '"Noto Sans", sans-serif' } : undefined}>
                <TitleWrapper>
                    <TitleText style={managed ? { paddingRight: 32 } : undefined}>{title}</TitleText>
                    {managed && <IconButton aria-label="Đóng" onClick={handleCloseWithAnimation} disabled={busy} sx={{ position: "absolute", top: 16, right: 16 }}><CloseIcon /></IconButton>}
                    {/* Render each item in contentText dynamically with adjusted padding */}
                    {contentText.map((text, index) => (
                        <WarningText key={index} paddingTop={getPaddingTop(index)}>
                            {text}
                        </WarningText>
                    ))}
                    {children}
                    <ReasonText style={managed ? { marginTop: 16 } : undefined}>
                        {reasonText}
                        <span className="required-asterisk">*</span>
                    </ReasonText>
                    <div style={{ width: "100%", display: "flex" }}>
                        {managed ? <TextField
                            multiline minRows={4} fullWidth required
                            inputProps={{ "aria-label": reasonText || "Lý do" }}
                            value={text} onChange={(event) => setText(event.target.value)}
                            disabled={busy}
                        /> : <TextNote
                            text={text}
                            setText={setText}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "1px",
                                flex: "1 0 0",
                                minHeight: "100px",
                                maxHeight: "400px",
                                alignSelf: "stretch",
                            }}
                        />}
                    </div>
                    {helperText && <Typography variant="caption" color="text.secondary">{helperText}</Typography>}
                </TitleWrapper>
                <ButtonWrapper style={managed ? { justifyContent: "flex-end", gap: 12 } : undefined}>
                    <CustomButton
                        title={cancelButtonText}
                        onClick={handleCloseWithAnimation}
                        disabled={busy}
                        style={{
                            paddingTop: 8,
                            paddingBottom: 8,
                            display: "flex",
                            justifyContent: "center",
                            width: managed ? "auto" : "60%",
                            minWidth: "120px",
                        }}
                    />
                    <CustomButton
                        title={buttonText ? buttonText : "Hủy đơn hàng"}
                        variant="contained"
                        onClick={() => handleConfirmWithAnimation(true)}
                        disabled={busy || (managed ? !text.trim() : !text)}
                        isLoading={busy}
                        sx={confirmColor ? { backgroundColor: confirmColor, "&:hover": { backgroundColor: confirmColor } } : undefined}
                        style={{
                            paddingTop: 8,
                            paddingBottom: 8,
                            display: "flex",
                            justifyContent: "center",
                            width: managed ? "auto" : "60%",
                            minWidth: "120px",
                        }}
                    />
                </ButtonWrapper>
            </ModalWrapper>
        </Modal>
    );
};

export default FailModal;
